/**
 * VolatilityPanel.jsx
 *
 * Shows ranging vs trending regime for each watched symbol.
 * Designed for scalpers who want to know:
 *   1. Is this stock ranging (good to fade) or trending (exit fast)?
 *   2. How volatile is it right now?
 *   3. Is RSI oscillating or drifting?
 *
 * Indicators:
 *   ADX(14)     — primary regime signal. <20 = ranging, >30 = trending.
 *   ATR%(14)    — volatility as % of price. Shows cost of a move.
 *   BB Width%   — band width relative to price. Squeeze = breakout pending.
 *   RSI Slope   — is RSI oscillating or drifting?  Near 0 = choppy (ranging).
 *   +DI / -DI   — directional bias inside the trend (when ADX > 20).
 */

const REGIME_THRESHOLDS = [
  { max: 15,  label: 'Strong Range', short: 'RANGE',  color: '#22d3ee', bg: '#22d3ee12', scalp: 'Ideal — fade extremes confidently' },
  { max: 20,  label: 'Ranging',      short: 'RANGE',  color: '#22d3ee', bg: '#22d3ee08', scalp: 'Good — mean-reversion favoured' },
  { max: 25,  label: 'Weak Trend',   short: 'MIXED',  color: '#f59e0b', bg: '#f59e0b08', scalp: 'Caution — trend developing, tighten stops' },
  { max: 35,  label: 'Trending',     short: 'TREND',  color: '#f97316', bg: '#f9731608', scalp: 'Risky — avoid fading, cut losers quickly' },
  { max: 999, label: 'Strong Trend', short: 'TREND↑', color: '#ef4444', bg: '#ef444408', scalp: 'Avoid mean-reversion — trend too strong' },
];

function getRegimeMeta(adx) {
  if (adx == null) return { label: 'No data', short: '—', color: 'var(--faint)', bg: 'transparent', scalp: '' };
  return REGIME_THRESHOLDS.find(t => adx < t.max) ?? REGIME_THRESHOLDS[REGIME_THRESHOLDS.length - 1];
}

// Mini bar gauge
function MiniBar({ value, min, max, color, pct: forcePct }) {
  const pct = forcePct ?? Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  return (
    <div style={{ flex: 1, height: 4, background: 'var(--bg)', borderRadius: 2,
      border: '1px solid var(--border)', overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color,
        borderRadius: 2, transition: 'width .5s, background .4s' }} />
    </div>
  );
}

// ADX gauge: 0–50 range, color shifts at 20 and 30
function ADXGauge({ adx }) {
  if (adx == null) return <span style={{ color: 'var(--faint)', fontSize: 11 }}>—</span>;
  const meta  = getRegimeMeta(adx);
  const pct   = Math.min(100, (adx / 50) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'monospace',
        color: meta.color, minWidth: 34, textAlign: 'right' }}>
        {adx.toFixed(1)}
      </div>
      <MiniBar forcePct={pct} color={meta.color} />
      <span style={{ fontSize: 10, fontWeight: 700, color: meta.color,
        letterSpacing: '.06em', minWidth: 48 }}>
        {meta.short}
      </span>
    </div>
  );
}

// RSI slope indicator: oscillating (near 0) vs drifting
function RSISlopeIndicator({ slope }) {
  if (slope == null) return <span style={{ color: 'var(--faint)', fontSize: 11 }}>—</span>;
  const abs  = Math.abs(slope);
  const drifting = abs > 4;
  const color    = drifting
    ? (slope > 0 ? 'var(--down)' : 'var(--up)')   // drift up = overbought pressure, drift dn = oversold pressure
    : 'var(--faint)';
  const label    = drifting
    ? (slope > 0 ? `↑ drifting up (${slope.toFixed(1)})` : `↓ drifting dn (${slope.toFixed(1)})`)
    : `≈ oscillating (${slope.toFixed(1)})`;
  return (
    <span style={{ fontSize: 11, fontFamily: 'monospace', color, fontWeight: drifting ? 600 : 400 }}>
      {label}
    </span>
  );
}

// Per-symbol row
function SymbolRow({ sym, state }) {
  if (!state) {
    return (
      <div style={{ padding: '10px 14px', borderRadius: 6, border: '1px solid var(--border)',
        background: 'var(--bg)', marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{sym}</span>
          <span style={{ fontSize: 11, color: 'var(--faint)' }}>
            {state?.barsLoaded != null
              ? `Accumulating bars ${state.barsLoaded}/${state.barsNeeded ?? 28}`
              : 'Waiting for data…'}
          </span>
        </div>
      </div>
    );
  }

  const { adx, pDI, mDI, atrPct, bbWidth, rsiSlope, price, bb, regime, coil } = state;
  const meta = getRegimeMeta(adx);
  const bullBias = pDI != null && mDI != null && pDI > mDI;
  const bearBias = pDI != null && mDI != null && mDI > pDI;

  return (
    <div style={{ padding: '12px 14px', borderRadius: 6,
      border: `1px solid ${coil?.coiling ? 'var(--down)' : adx != null && adx < 20 ? meta.color + '60' : 'var(--border)'}`,
      background: coil?.coiling ? 'var(--down-soft)' : meta.bg, marginBottom: 8 }}>

      {/* Multi-day coil warning — highest priority */}
      {coil?.coiling && (
        <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 5,
          background: 'var(--down)', color: 'var(--bg)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em' }}>
            ⊟ MULTI-DAY COIL — STAND DOWN
          </div>
          <div style={{ fontSize: 10, marginTop: 3, opacity: 0.95 }}>
            {coil.daysInBand} closes in a {coil.bandPct}% band (${coil.bandLow}–${coil.bandHigh}).
            Don't scalp the pin — wait for the break.
          </div>
          <div style={{ fontSize: 9, marginTop: 3, fontFamily: 'monospace', opacity: 0.9 }}>
            Break up &gt; ${coil.bandHigh} ({coil.distToBreakUp}% away) · break dn &lt; ${coil.bandLow} ({coil.distToBreakDn}% away)
          </div>
        </div>
      )}

      {/* Header: symbol + regime badge + scalp hint */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
            {sym}
          </span>
          {price != null && (
            <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--faint)', marginLeft: 8 }}>
              ${price.toFixed(2)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 3,
            letterSpacing: '.08em', background: meta.color + '20',
            color: meta.color, border: `1px solid ${meta.color}60`,
          }}>
            {meta.label.toUpperCase()}
          </span>
          <span style={{ fontSize: 9, color: meta.color, textAlign: 'right', maxWidth: 200 }}>
            {meta.scalp}
          </span>
        </div>
      </div>

      {/* Indicator grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '5px 10px', alignItems: 'center' }}>

        {/* ADX */}
        <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>
          ADX (14)
          <span style={{ fontSize: 8, color: 'var(--faint)', display: 'block', fontWeight: 400 }}>
            &lt;20 range · &gt;30 trend
          </span>
        </span>
        <ADXGauge adx={adx} />

        {/* +DI / -DI directional bias (only meaningful when ADX > 20) */}
        {pDI != null && mDI != null && adx > 20 && (
          <>
            <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>
              Direction
              <span style={{ fontSize: 8, color: 'var(--faint)', display: 'block', fontWeight: 400 }}>
                +DI vs −DI bias
              </span>
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700,
                color: bullBias ? 'var(--up)' : 'var(--down)' }}>
                {bullBias ? '▲ Bullish' : '▼ Bearish'}
              </span>
              <span style={{ fontSize: 10, color: 'var(--faint)', fontFamily: 'monospace' }}>
                +{pDI.toFixed(1)} / −{mDI.toFixed(1)}
              </span>
            </div>
          </>
        )}

        {/* ATR % */}
        <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>
          ATR% (14)
          <span style={{ fontSize: 8, color: 'var(--faint)', display: 'block', fontWeight: 400 }}>
            move cost per bar
          </span>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {atrPct != null ? (
            <>
              <span style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 700,
                color: atrPct > 0.5 ? 'var(--warn)' : 'var(--text)' }}>
                {atrPct.toFixed(3)}%
              </span>
              <MiniBar forcePct={Math.min(100, atrPct * 100)} color={atrPct > 0.5 ? 'var(--warn)' : 'var(--accent)'} />
              <span style={{ fontSize: 9, color: 'var(--faint)', minWidth: 60 }}>
                ≈${((atrPct / 100) * (price ?? 0)).toFixed(2)}/bar
              </span>
            </>
          ) : <span style={{ fontSize: 11, color: 'var(--faint)' }}>—</span>}
        </div>

        {/* BB Width % */}
        <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>
          BB Width%
          <span style={{ fontSize: 8, color: 'var(--faint)', display: 'block', fontWeight: 400 }}>
            squeeze = breakout risk
          </span>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {bbWidth != null ? (
            <>
              <span style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 700,
                color: bbWidth < 0.5 ? 'var(--warn)' : 'var(--text)' }}>
                {bbWidth.toFixed(2)}%
              </span>
              <MiniBar forcePct={Math.min(100, bbWidth * 20)} color={bbWidth < 0.5 ? 'var(--warn)' : 'var(--accent)'} />
              <span style={{ fontSize: 9, color: bbWidth < 0.5 ? 'var(--warn)' : 'var(--faint)', minWidth: 50 }}>
                {bbWidth < 0.5 ? '⚠ squeeze' : bbWidth > 2 ? 'expanding' : 'normal'}
              </span>
            </>
          ) : <span style={{ fontSize: 11, color: 'var(--faint)' }}>—</span>}
        </div>

        {/* RSI Slope */}
        <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 600 }}>
          RSI Slope
          <span style={{ fontSize: 8, color: 'var(--faint)', display: 'block', fontWeight: 400 }}>
            ≈0 = choppy (good)
          </span>
        </span>
        <RSISlopeIndicator slope={rsiSlope} />

      </div>

      {/* BB price position bar */}
      {bb && price != null && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 9, color: 'var(--faint)', marginBottom: 3, display: 'flex', justifyContent: 'space-between' }}>
            <span>${bb.lower.toFixed(2)} BB lower</span>
            <span>Price in band</span>
            <span>BB upper ${bb.upper.toFixed(2)}</span>
          </div>
          <div style={{ height: 6, background: 'var(--bg)', borderRadius: 3,
            border: '1px solid var(--border)', position: 'relative', overflow: 'visible' }}>
            {/* Band range */}
            <div style={{
              position: 'absolute',
              left: `${Math.max(0, ((bb.lower - (bb.lower - bb.std)) / (bb.upper + bb.std - (bb.lower - bb.std))) * 100)}%`,
              right: `${Math.max(0, 100 - ((bb.upper - (bb.lower - bb.std)) / (bb.upper + bb.std - (bb.lower - bb.std))) * 100)}%`,
              top: 0, bottom: 0, background: 'var(--accent)', opacity: 0.15, borderRadius: 2,
            }} />
            {/* Price marker */}
            {(() => {
              const range = (bb.upper + bb.std) - (bb.lower - bb.std);
              const pct   = Math.max(2, Math.min(98, ((price - (bb.lower - bb.std)) / range) * 100));
              const col   = price < bb.lower ? 'var(--up)' : price > bb.upper ? 'var(--down)' : 'var(--text)';
              return (
                <div style={{
                  position: 'absolute', left: `${pct}%`, top: -2, bottom: -2, width: 3,
                  background: col, borderRadius: 2, transform: 'translateX(-50%)',
                  boxShadow: `0 0 4px ${col}`,
                }} />
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default function VolatilityPanel({ symbolStates, watchedSymbols }) {
  const symbols = watchedSymbols ?? ['SPY'];
  const hasData = symbols.some(s => symbolStates?.[s] != null);

  return (
    <div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, fontSize: 10, color: 'var(--faint)' }}>
        {REGIME_THRESHOLDS.slice(0, 4).map(t => (
          <span key={t.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: t.color, display: 'inline-block' }} />
            <span style={{ color: t.color }}>{t.short}</span>
            <span>{t.label} (ADX {t === REGIME_THRESHOLDS[0] ? '<15' : t === REGIME_THRESHOLDS[1] ? '15–20' : t === REGIME_THRESHOLDS[2] ? '20–25' : '25–35'})</span>
          </span>
        ))}
      </div>

      {!hasData && (
        <div style={{ fontSize: 12, color: 'var(--faint)', textAlign: 'center', padding: '24px 0' }}>
          Start the engine to load volatility data.
          <div style={{ marginTop: 4, fontSize: 11 }}>ADX needs 28 bars (~28 minutes) to initialise.</div>
        </div>
      )}

      {symbols.map(sym => (
        <SymbolRow key={sym} sym={sym} state={symbolStates?.[sym] ?? null} />
      ))}
    </div>
  );
}
